import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { diceSearchIdentity } from "../../common/utils";

/**
 * Tra lịch sử chơi game xúc xắc của MỘT user theo uid, mới nhất trước.
 *
 * Nguồn dữ liệu là engine xúc xắc mới (service luckydice), KHÔNG phải cặp kind legacy
 * DiceGameInfoHistory/DiceGamePlayerHistory — hai kind đó do Cloud Function ghi và đã đứng yên
 * từ khi cờ engineClock bật.
 */

const API = "/luckydice/web/dice-history";

function formatTime(millis) {
  if (!millis) return "";
  return new Date(millis).toLocaleString("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatIcoin(value) {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("vi-VN");
}

/** Thông điệp lỗi thật do backend trả về (nếu có) — dài nên toast cần autoClose lâu hơn. */
function serverMessage(err) {
  return err && err.response && err.response.data ? err.response.data.message : null;
}

const PAGE_SIZE = 100;

/** "YYYY-MM-DD" (giờ máy admin) → mốc epoch ms; endOfDay lấy 00:00 ngày kế vì backend lọc [from, to). */
function toMillis(dateStr, endOfDay) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return endOfDay ? d.getTime() + 24 * 3600 * 1000 : d.getTime();
}

function DiceUserHistoryPage() {
  const [keyword, setKeyword] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // gameId đang gọi nạp bù → khoá đúng nút đó, tránh admin bấm dồn
  const [compensating, setCompensating] = useState(null);

  /** Body dùng chung cho cả tìm mới lẫn tải thêm — giữ nguyên bộ lọc khi sang trang. */
  function buildBody(cursor) {
    const value = keyword.trim();
    // Số vừa int32 → uid. Facebook id toàn số dài hơn gửi nguyên chuỗi userId.
    const body = diceSearchIdentity(value);
    body.startDateMillis = toMillis(fromDate, false);
    body.endDateMillis = toMillis(toDate, true);
    body.limit = PAGE_SIZE;
    if (cursor) body.cursor = cursor;
    return body;
  }

  async function search() {
    if (!keyword.trim()) {
      toast.warn("Nhập uid hoặc userId");
      return;
    }
    if (fromDate && toDate && toMillis(fromDate, false) > toMillis(toDate, false)) {
      toast.warn("Khoảng ngày không hợp lệ: 'Từ ngày' đang sau 'Đến ngày'");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/user", buildBody(null));
      setData(res.data);
      if (!res.data.rounds || res.data.rounds.length === 0) {
        toast.info("Không có ván nào khớp điều kiện");
      }
    } catch (err) {
      setData(null);
      if (err.response && err.response.status === 404) {
        toast.error("Không tìm thấy user với uid này");
      } else {
        // Backend (DiceHistoryExceptionHandler) trả nguyên nhân thật → hiện thẳng, khỏi mò log
        toast.error(serverMessage(err) || "Lỗi khi tra cứu lịch sử");
      }
    } finally {
      setLoading(false);
    }
  }

  /** Nối trang kế vào cuối bảng, giữ nguyên phần đã tải. */
  async function loadMore() {
    if (!data || !data.nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/user", buildBody(data.nextCursor));
      setData({
        ...res.data,
        rounds: (data.rounds || []).concat(res.data.rounds || []),
      });
    } catch (err) {
      toast.error(serverMessage(err) || "Lỗi khi tải thêm");
    } finally {
      setLoadingMore(false);
    }
  }

  async function compensate(round) {
    if (!window.confirm(
      "Nạp bù cho ván " + round.gameId + "?\n\n" +
      "Hệ thống tự tính lại tiền thắng từ số cược của ván. Bấm nhiều lần cũng chỉ cộng một lần."
    )) {
      return;
    }
    setCompensating(round.gameId);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/compensate", {
        gameId: round.gameId,
        userId: data.userId,
      });
      if (res.data.ok) {
        toast.success(res.data.message);
        search(); // nạp lại để cột trạng thái phản ánh đúng
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(
        err.response && err.response.status === 403
          ? "Bạn không có quyền nạp bù (cần ICOIN-MANAGER)"
          : "Nạp bù thất bại"
      );
    } finally {
      setCompensating(null);
    }
  }

  return (
    <div className="container" style={{ marginTop: "100px", marginBottom: "100px" }}>
      <h1 className="text-center">Lịch sử game xúc xắc theo user</h1>

      <div className="form-inline justify-content-center" style={{ marginTop: "30px" }}>
        <label htmlFor="dice-uid" style={{ paddingRight: "15px" }}>uid</label>
        <input
          id="dice-uid"
          className="form-control"
          style={{ width: "260px", marginRight: "15px" }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && search()}
          placeholder="Nhập uid (hoặc userId)"
        />

        <label htmlFor="dice-from" style={{ paddingRight: "10px" }}>Từ ngày</label>
        <input
          id="dice-from"
          type="date"
          className="form-control"
          style={{ marginRight: "15px" }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <label htmlFor="dice-to" style={{ paddingRight: "10px" }}>Đến ngày</label>
        <input
          id="dice-to"
          type="date"
          className="form-control"
          style={{ marginRight: "15px" }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <button className="btn btn-primary" onClick={search} disabled={loading}>
          {loading ? "Đang tìm..." : "Tìm kiếm"}
        </button>
      </div>
      <p className="text-center text-muted" style={{ marginTop: "8px", fontSize: "13px" }}>
        Bỏ trống khoảng ngày để xem toàn bộ lịch sử.
      </p>

      {data && (
        <div style={{ marginTop: "30px" }}>
          <h5>
            {data.name} — uid {data.uid} — userId {data.userId}
            <span className="text-muted" style={{ fontSize: "14px", marginLeft: "10px" }}>
              (đang hiển thị {data.rounds ? data.rounds.length : 0} ván
              {data.hasMore ? ", còn nữa" : ""})
            </span>
          </h5>

          <table className="table table-bordered table-hover" style={{ marginTop: "15px" }}>
            <thead className="table-primary">
              <tr>
                <th>No</th>
                <th>Id ván game</th>
                <th>Thời gian</th>
                <th>Kết quả</th>
                <th>BIG</th>
                <th>SMALL</th>
                <th>TRIPLE</th>
                <th>Thắng/Thua</th>
                <th>Cộng tiền</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.rounds && data.rounds.length > 0 ? (
                data.rounds.map((round, index) => (
                  <tr key={round.gameId}>
                    <td>{index + 1}</td>
                    <td>{round.gameId}</td>
                    <td>{formatTime(round.dateTime)}</td>
                    <td>{round.resultText || "—"}</td>
                    <td>{formatIcoin(round.icoinBig)}</td>
                    <td>{formatIcoin(round.icoinSmall)}</td>
                    <td>{formatIcoin(round.icoinTriple)}</td>
                    <td style={{ color: round.icoinResult < 0 ? "#dc3545" : "#28a745" }}>
                      {formatIcoin(round.icoinResult)}
                    </td>
                    <td>
                      {round.status === "FAILED" ? (
                        <span className="badge badge-danger">LỖI</span>
                      ) : (
                        <span className="badge badge-success">OK</span>
                      )}
                    </td>
                    <td>
                      {round.canCompensate && (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => compensate(round)}
                          disabled={compensating === round.gameId}
                        >
                          {compensating === round.gameId ? "Đang nạp..." : "Nạp bù"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center">Không có dữ liệu</td>
                </tr>
              )}
            </tbody>
          </table>

          {data.hasMore && (
            <div className="text-center" style={{ marginTop: "15px" }}>
              <button className="btn btn-outline-primary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Đang tải..." : "Tải thêm"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DiceUserHistoryPage;
